import {
  ContentNugget,
  ContentCompetency,
  Scholarship,
  CareerPathResponse,
  Internship,
  College,
  Employer,
  Tool,
  Major,
  Industry,
  Coach,
  DeepDive,
  toolTransform,
  collegeTransform,
  competencyTransform,
  internshipTransform,
  scholarshipTransform,
  careerPathTransform,
  majorTransform,
  industryTransform,
  employerTransform,
  coachTransform,
  deepDiveTransform,
  playlistTransform,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import {
  StreamedMessageResponse,
  StreamMessage,
  DeltaStreamCallbacks,
  NuggetReference,
  FollowUpButtonPayload,
  isChartSpec,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import {
  SUBSESSION_FEATURED_RESOURCES,
  BoxType,
} from '@backrs_ui/grow-ui/src/components/PageShell/resourceTabs/InfoBox.config';
import {
  ChartSpec,
  ResourcesContent,
  PromptItem,
  ResourceKey,
  getNuggetOutputResources,
  isStructuredSession,
  LubavOfficeHoursSubsessionType,
  LubavSessionType,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';

import { jsonrepair } from 'jsonrepair';

export type NuggetsByType = Record<string, NuggetReference[]>;

export type ToolEvent = Readonly<{
  tool_name: string;
  status: 'started' | 'completed';
  display_name: string;
  output?: string;
}>;

export type StreamedMessage = Readonly<{
  query_id: string;
  response_id: string;
  response: string;
  suggested_prompts: PromptItem[];
  ask_to_regenerate?: boolean;
  nugget_ids?: NuggetReference[];
  playbook_id?: number;
  featured_nugget_types?: string[];
  follow_up_buttons?: FollowUpButtonPayload[];
  tool_event?: ToolEvent;
}>;

const defaultMessage: StreamedMessage = {
  query_id: '',
  response_id: '',
  response: '',
  suggested_prompts: [],
};

export const parseStreamChunk = (accumulatedResponse: string): StreamedMessage => {
  try {
    const fixedJson = jsonrepair(accumulatedResponse);

    const parsed = JSON.parse(fixedJson);

    return parsed;
  } catch {
    return defaultMessage;
  }
};

export const hasIdFields = (chunk: StreamedMessage): boolean => {
  return !!chunk.nugget_ids?.length;
};

export const extractNuggetReferencesFromText = (
  text: string,
  chunk: StreamedMessage,
): StreamedMessage => {
  // matches pattern: [text](nuggettype:ID)
  const pattern = /\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;

  const matches = Array.from(text.matchAll(pattern));

  const existingNuggets = chunk.nugget_ids || [];
  const existingNuggetsMap = new Map(
    existingNuggets.map((nugget) => [`${nugget.type}:${nugget.id}`, nugget]),
  );

  matches.forEach((match) => {
    const [, , nuggetType, nuggetId] = match;
    const normalizedType = nuggetType.toLowerCase().trim();

    const key = `${normalizedType}:${nuggetId.trim()}`;

    if (!existingNuggetsMap.has(key)) {
      existingNuggetsMap.set(key, {
        type: normalizedType,
        id: nuggetId.trim(),
      });
    }
  });

  return {
    ...chunk,
    nugget_ids: Array.from(existingNuggetsMap.values()),
  };
};

export const isValidMarkdown = (markdown: string): boolean => {
  const isEven = (count: number): boolean => count % 2 === 0;

  const hasBalancedFormatting = (text: string): boolean => {
    const boldCount = (text.match(/\*\*/g) || []).length;
    const italicCount = (text.match(/_/g) || []).length;

    return isEven(boldCount) && isEven(italicCount);
  };

  const hasValidLists = (text: string): boolean => {
    return text.split('\n').every((line) => {
      const trimmed = line.trim();
      const isBullet = /^[-*+]\s+.+/.test(trimmed);
      const isNumbered = /^\d+\.\s+.+/.test(trimmed);
      const startsLikeList = /^[-*+]|^\d+\./.test(trimmed);

      return startsLikeList ? isBullet || isNumbered : true;
    });
  };

  const hasValidHeaders = (text: string): boolean => {
    return text.split('\n').every((line) => {
      const trimmed = line.trim();
      const isHeader = /^#{1,6}(?!#)/.test(trimmed);
      const isValid = /^#{1,6} +\S/.test(trimmed);

      return isHeader ? isValid : true;
    });
  };

  const hasNoTrailingBackslash = (text: string): boolean => {
    return !/\\+\s*$/.test(text);
  };

  return (
    hasBalancedFormatting(markdown) &&
    hasValidLists(markdown) &&
    hasValidHeaders(markdown) &&
    hasNoTrailingBackslash(markdown)
  );
};

// map nugget types identifiers to resource keys
export const nuggetTypeToResourceKey: Record<string, ResourceKey> = {
  tool: 'tools',
  job_opportunity: 'internships',
  competency: 'competencies',
  story: 'stories',
  skill: 'learning',
  college: 'colleges',
  career_path: 'career_paths',
  scholarship: 'scholarships',
  major: 'majors',
  industry: 'industries',
  employer: 'employers',
  suggested_playbook: 'suggested_playbooks',
  coach: 'coaches',
  deep_dive: 'deep_dives',
};

export const resourceKeyToTab: Record<ResourceKey, BoxType> = {
  tools: 'TOOLS',
  suggested_playbooks: 'TOOLS',
  competencies: 'TOOLS',
  internships: 'INTERNSHIPS',
  career_paths: 'CAREER_PATHS',
  scholarships: 'SCHOLARSHIPS',
  employers: 'EMPLOYERS',
  industries: 'INDUSTRIES',
  colleges: 'COLLEGES',
  majors: 'MAJORS',
  stories: 'LEARNING',
  learning: 'LEARNING',
  resources: 'RESOURCES',
  coaches: 'COACHES',
  deep_dives: 'COACHES',
};

export const hydratedDataToContentNugget = (
  nuggetType: string,
  data: Record<string, unknown>,
): ContentNugget | null => {
  if (Array.isArray(data)) return null;

  const resourceKey = nuggetTypeToResourceKey[nuggetType];

  switch (resourceKey) {
    case 'tools':
      return toolTransform(data as Tool);
    case 'colleges':
      return collegeTransform(data as College);
    case 'competencies':
      return competencyTransform(data as ContentCompetency);
    case 'internships':
      return internshipTransform(data as Internship);
    case 'scholarships':
      return scholarshipTransform(data as Scholarship);
    case 'career_paths':
      return careerPathTransform(data as CareerPathResponse);
    case 'majors':
      return majorTransform(data as Major);
    case 'industries':
      return industryTransform(data as Industry);
    case 'employers':
      return employerTransform(data as Employer);
    case 'coaches':
      return coachTransform(data as Coach);
    case 'deep_dives':
      return deepDiveTransform(data as DeepDive);
    case 'suggested_playbooks':
      return playlistTransform(data as PriorityPlaylist);
    default:
      return data as ContentNugget;
  }
};

// reverse mapping: resource keys to nugget types
export const resourceKeyToNuggetType: Record<ResourceKey, string> = Object.fromEntries(
  Object.entries(nuggetTypeToResourceKey).map(([nuggetType, resourceKey]) => [
    resourceKey,
    nuggetType,
  ]),
) as Record<ResourceKey, string>;

// Subsessions feature their configured resources; nugget-output sessions feature their main
// output type. Other structured sessions feature nothing.
const getFeaturedResourceKeys = (sessionId: string | null | undefined): ResourceKey[] => {
  if (!sessionId) return [];
  if (sessionId in SUBSESSION_FEATURED_RESOURCES) {
    return SUBSESSION_FEATURED_RESOURCES[sessionId as LubavOfficeHoursSubsessionType];
  }

  return getNuggetOutputResources(sessionId);
};

// get featured nugget types for a session from the config
const getSubsessionFeaturedNuggetTypes = (
  sessionId: LubavSessionType | null | undefined,
): Set<string> => {
  const featuredResourceKeys = getFeaturedResourceKeys(sessionId);

  return new Set(
    featuredResourceKeys.map((resourceKey) => resourceKeyToNuggetType[resourceKey]).filter(Boolean),
  );
};

// nugget types that already have a featured item in the session's resources
const getAlreadyFeaturedTypes = (resources: ResourcesContent | undefined): string[] =>
  Object.entries(resources ?? {})
    .filter(([, data]) =>
      data?.content?.content?.some((item) => 'isFeatured' in item && item.isFeatured === true),
    )
    .map(([key]) => resourceKeyToNuggetType[key as ResourceKey])
    .filter(Boolean);

export const transformIdsToResourcesContent = (
  chunk: Pick<StreamedMessage, 'nugget_ids' | 'featured_nugget_types'>,
  existingResources: ResourcesContent | undefined,
  sessionId?: LubavSessionType | null,
): ResourcesContent => {
  const newResources: Record<string, unknown> = { ...existingResources };

  if (!chunk.nugget_ids?.length) {
    return newResources as ResourcesContent;
  }

  // featuring is sticky: once a type has a featured nugget, later finds of that type join its tab
  const subsessionFeaturedTypes = getSubsessionFeaturedNuggetTypes(sessionId);
  const responseFeaturedTypes = isStructuredSession(sessionId)
    ? []
    : chunk.featured_nugget_types || [];
  const featuredTypes = new Set(
    Array.from(subsessionFeaturedTypes)
      .concat(responseFeaturedTypes)
      .concat(getAlreadyFeaturedTypes(existingResources)),
  );

  // group nuggets by type
  const nuggetsByType = chunk.nugget_ids.reduce((acc: NuggetsByType, nugget) => {
    if (!acc[nugget.type]) {
      acc[nugget.type] = [];
    }

    acc[nugget.type].push(nugget);

    return acc;
  }, {});

  // transform each nugget type into the format into ResourcesContent
  Object.entries(nuggetsByType).forEach(([nuggetType, nuggets]) => {
    const resourceKey = nuggetTypeToResourceKey[nuggetType];
    const isFeatured = featuredTypes.has(nuggetType);
    // featured nuggets go to their own named tab, others to SUGGESTED
    const tab = isFeatured ? resourceKeyToTab[resourceKey] : 'SUGGESTED';

    if (resourceKey && nuggets.length) {
      const existingContent =
        (newResources[resourceKey] as ResourcesContent[typeof resourceKey])?.content?.content || [];
      const existingIds = new Set(existingContent.map((item) => String(item.id)));

      const newItems = nuggets
        .filter((nugget) => !existingIds.has(nugget.id))
        .map((nugget) => ({
          ...nugget.contentNugget,
          tab,
          isFeatured,
        }));

      newResources[resourceKey] = {
        ...(newResources[resourceKey] as Record<string, unknown>),
        content: {
          content: [...existingContent, ...newItems],
        },
      };
    }
  });

  return newResources as ResourcesContent;
};

export const createTextStreamHandler =
  (onChunk?: (chunk: string) => void) =>
  async (response: Response): Promise<string> => {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) return '';

    let streamResult = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      streamResult += decoder.decode(value);
      onChunk?.(streamResult);
    }

    return streamResult;
  };

// Smooths bursty token arrival by paint-typing characters at an adaptive
// rate. Each frame emits enough of the backlog to drain it over
// SMOOTH_TARGET_MS of wall clock, regardless of frame rate or server speed.
// flush() paints everything remaining — call it when the stream ends.
const SMOOTH_TARGET_MS = 150;

const createSmoothedEmitter = (emit: (text: string) => void) => {
  let emitted = '';
  let target = '';
  let rafId: number | null = null;
  let lastTickMs = 0;

  const tick = (tickNow: number) => {
    rafId = null;
    const pending = target.length - emitted.length;

    if (pending <= 0) return;

    const frameMs = lastTickMs === 0 ? 16 : Math.max(1, tickNow - lastTickMs);

    lastTickMs = tickNow;
    const framesToDrain = Math.max(1, SMOOTH_TARGET_MS / frameMs);
    const charsThisFrame = Math.max(1, Math.ceil(pending / framesToDrain));

    emitted = target.slice(0, emitted.length + charsThisFrame);
    emit(emitted);

    if (emitted.length < target.length) rafId = requestAnimationFrame(tick);
  };

  return {
    push: (accumulated: string) => {
      target = accumulated;
      if (rafId === null) rafId = requestAnimationFrame(tick);
    },
    flush: () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (emitted !== target) {
        emitted = target;
        emit(emitted);
      }
    },
  };
};

export const createDeltaStreamHandler = (callbacks: DeltaStreamCallbacks) => {
  return async (response: Response): Promise<StreamedMessageResponse> => {
    const reader = response.body?.getReader();

    if (!reader) {
      return { response_id: undefined };
    }

    let accumulatedContent = '';
    const deltaEmitter = callbacks.onDelta ? createSmoothedEmitter(callbacks.onDelta) : null;
    let queryId: number | null = null;
    let responseId: number | null = null;
    const accumulatedNuggets: NuggetReference[] = [];
    const accumulatedPrompts: PromptItem[] = [];
    const accumulatedFeaturedTypes: string[] = [];
    const accumulatedFollowUpButtons: FollowUpButtonPayload[] = [];
    const accumulatedCharts: ChartSpec[] = [];
    let accumulatedPlaybookId: number | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const text = new TextDecoder().decode(value);
        const lines = text.split('\n').filter((line) => line.startsWith('data: '));

        for (const line of lines) {
          const jsonStr = line.replace(/^data: /, '');

          try {
            const message = JSON.parse(jsonStr) as StreamMessage;

            switch (message.type) {
              case 'meta':
                queryId = message.query_id;
                responseId = message.response_id;
                callbacks.onMeta?.(message);
                break;

              case 'delta':
                accumulatedContent += message.content;
                if (deltaEmitter) {
                  deltaEmitter.push(accumulatedContent);
                } else {
                  callbacks.onDelta?.(accumulatedContent);
                }

                break;

              case 'artifact':
                callbacks.onArtifact?.(message);
                break;

              case 'chart':
                if (isChartSpec(message.spec)) {
                  accumulatedCharts.push(message.spec);
                  callbacks.onChart?.(message);
                } else {
                  console.warn('chart event with an unrecognized spec:', message);
                }

                break;

              case 'tool':
                callbacks.onTool?.(message);
                break;

              case 'nugget': {
                const contentNugget = hydratedDataToContentNugget(
                  message.nugget_type,
                  message.data,
                );

                if (contentNugget) {
                  accumulatedNuggets.push({
                    type: message.nugget_type,
                    id: String(contentNugget.id),
                    contentNugget,
                  });
                  callbacks.onNugget?.(message);
                }

                break;
              }

              case 'prompt':
                accumulatedPrompts.push(message.prompt);
                callbacks.onPrompt?.(message);
                break;

              case 'playbook':
                accumulatedPlaybookId = message.playbook_id;
                callbacks.onPlaybook?.(message);
                break;

              case 'featured_type':
                accumulatedFeaturedTypes.push(message.nugget_type);
                callbacks.onFeaturedType?.(message);
                break;

              case 'component':
                callbacks.onComponent?.(message);
                break;

              case 'follow_up_buttons':
                if (Array.isArray(message.buttons)) {
                  accumulatedFollowUpButtons.push(...message.buttons);
                } else {
                  console.warn('follow_up_buttons event with non-array buttons:', message);
                }

                break;

              case 'done':
                deltaEmitter?.flush();
                callbacks.onDone?.({
                  ...message,
                  response: accumulatedContent,
                  query_id: queryId,
                  response_id: responseId,
                  suggested_prompts: accumulatedPrompts,
                  nugget_ids: accumulatedNuggets,
                  playbook_id: accumulatedPlaybookId,
                  featured_nugget_types: accumulatedFeaturedTypes,
                  follow_up_buttons: accumulatedFollowUpButtons,
                  charts: accumulatedCharts,
                });
                break;

              default:
                console.warn(
                  'Unknown stream event type:',
                  (message as { type: string }).type,
                  message,
                );
            }
          } catch (error) {
            console.warn('Failed to parse stream message:', jsonStr, error);
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        deltaEmitter?.flush();

        return { response_id: responseId ?? undefined };
      }

      throw error;
    } finally {
      deltaEmitter?.flush();
      reader.releaseLock();
    }

    return { response_id: responseId ?? undefined };
  };
};

const transformResourceEntry = (
  key: ResourceKey,
  data: ResourcesContent[ResourceKey],
  featuredKeys: Set<ResourceKey>,
) => {
  const isFeatured = featuredKeys.has(key);
  const tab = isFeatured ? resourceKeyToTab[key] : 'SUGGESTED';

  return {
    ...data,
    content: {
      content: (data?.content?.content ?? []).map((item) => ({
        ...item,
        tab,
        isFeatured,
      })),
    },
  };
};

// Recalled session resources arrive as raw records, not hydrated nuggets.
export const hydrateSessionResources = (
  resources: Partial<ResourcesContent>,
  sessionId?: LubavOfficeHoursSubsessionType | StructuredSessionId,
  featuredNuggetTypes?: readonly string[],
): ResourcesContent => {
  const withNuggets = Object.fromEntries(
    Object.entries(resources).map(([key, data]) => [
      key,
      {
        ...data,
        content: {
          content: (data?.content?.content ?? []).map(
            (item: Record<string, unknown>) =>
              hydratedDataToContentNugget(item.nugget_type as string, item) ?? item,
          ),
        },
      },
    ]),
  );

  return addTabAndFeaturedToResources(withNuggets, sessionId, featuredNuggetTypes);
};

const addTabAndFeaturedToResources = (
  resources: Partial<ResourcesContent>,
  sessionId?: LubavOfficeHoursSubsessionType | StructuredSessionId,
  featuredNuggetTypes?: readonly string[],
): ResourcesContent => {
  const staticKeys = getFeaturedResourceKeys(sessionId);
  const recalledKeys = isStructuredSession(sessionId)
    ? []
    : (featuredNuggetTypes ?? [])
        .map((nuggetType) => nuggetTypeToResourceKey[nuggetType])
        .filter((key): key is ResourceKey => Boolean(key));
  const featuredKeys = new Set<ResourceKey>([...staticKeys, ...recalledKeys]);

  return Object.fromEntries(
    Object.entries(resources).map(([key, data]) => [
      key,
      transformResourceEntry(key as ResourceKey, data, featuredKeys),
    ]),
  ) as ResourcesContent;
};
